import Button from "react-bootstrap/Button";
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import CmsPageSkeleton from '../components/CmsPageSkeleton';

import {
    FiFileText, FiSearch, FiEdit3, FiTrash2, FiPlus,
    FiSettings, FiDatabase,
} from 'react-icons/fi';

import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/StAuthGiver';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv !== 'aws');

export const StCmsBlogEntries = () => {
    const { loginAction, logOut } = useAuth();
    const navigate = useNavigate();
    const { schemaId: paramSchemaId } = useParams();
    const schemaId = paramSchemaId || 'blog';

    const [loading, setLoading] = useState(true);
    const [loading2, setLoading2] = useState(false);
    const [entries, setEntries] = useState([]);
    const [filteredList, setFilteredList] = useState([]);

    const [siteId] = useState(getCurSiteId() || '');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const schemaDisplayName = schemaId.charAt(0).toUpperCase() + schemaId.slice(1).replace(/_/g, ' ');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchEntries = async (isRefresh = false) => {
        try {
            isRefresh ? setLoading2(true) : setLoading(true);
            const rcJson = await callApi(`/sites/${siteId}/cms/schemas/${schemaId}/entries`, 'GET', {}, {});

            if (rcJson.disposition === 'logout') { logOut(); return; }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                if (rcJson.respBody?.etac) loginAction(null, rcJson.respBody.etac);
                const respData = rcJson.respBody.data || rcJson.respBody;
                const responseEntries = respData.entries || respData.items || [];
                setEntries(responseEntries);
                setFilteredList(responseEntries);
            } else {
                Tdebug && console.log('Failed to fetch entries:', rcJson.message);
                toast.error('Failed to load entries');
            }
        } catch (err) {
            Tdebug && console.log('Error fetching entries:', err);
            toast.error('Failed to load entries');
        } finally {
            isRefresh ? setLoading2(false) : setLoading(false);
        }
    };

    useEffect(() => {
        filterData();
    }, [searchTerm, statusFilter, entries]);

    // Reset and re-fetch when schemaId changes
    useEffect(() => {
        setSearchTerm('');
        setStatusFilter('all');
        fetchEntries(false);
    }, [schemaId, siteId]);

    const filterData = () => {
        let filtered = entries;

        if (searchTerm) {
            filtered = filtered.filter(item =>
                (item.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.slug || '').toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(item => item.status === statusFilter);
        }

        setFilteredList(filtered);
    };

    const refreshEntries = () => {
        fetchEntries(true);
    };

    const publishEntry = async (entryId) => {
        try {
            setActionLoading(true);
            const url = `/sites/${siteId}/cms/schemas/${schemaId}/entries/${entryId}/publish`;
            const rcJson = await callApi(url, 'POST', {}, JSON.stringify({}));

            if (rcJson.disposition === 'logout') { logOut(); return; }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                toast.success('Entry published successfully');
                refreshEntries();
            } else {
                toast.error(rcJson.message || 'Failed to publish entry');
            }
        } catch (err) {
            toast.error('Failed to publish entry');
            Tdebug && console.log(err);
        } finally {
            setActionLoading(false);
        }
    };

    const unpublishEntry = async (entryId) => {
        try {
            setActionLoading(true);
            const url = `/sites/${siteId}/cms/schemas/${schemaId}/entries/${entryId}/unpublish`;
            const rcJson = await callApi(url, 'POST', {}, JSON.stringify({}));

            if (rcJson.disposition === 'logout') { logOut(); return; }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                toast.success('Entry unpublished successfully');
                refreshEntries();
            } else {
                toast.error(rcJson.message || 'Failed to unpublish entry');
            }
        } catch (err) {
            toast.error('Failed to unpublish entry');
            Tdebug && console.log(err);
        } finally {
            setActionLoading(false);
        }
    };

    const deleteEntry = async () => {
        if (!selectedEntry) return;

        try {
            setActionLoading(true);
            const url = `/sites/${siteId}/cms/schemas/${schemaId}/entries/${selectedEntry.entry_id}`;
            const rcJson = await callApi(url, 'DELETE', {}, JSON.stringify({}));

            if (rcJson.disposition === 'logout') { logOut(); return; }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                toast.success('Entry deleted successfully');
                setShowDeleteModal(false);
                setSelectedEntry(null);
                refreshEntries();
            } else {
                toast.error(rcJson.message || 'Failed to delete entry');
            }
        } catch (err) {
            toast.error('Failed to delete entry');
            Tdebug && console.log(err);
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const handleEditEntry = (entry) => {
        if (schemaId === 'blog') {
            navigate(`/${siteId}/cms/blog/${entry.entry_id}`);
        } else {
            navigate(`/${siteId}/cms/entries/${schemaId}/${entry.entry_id}`);
        }
    };

    const handleNewEntry = () => {
        if (schemaId === 'blog') {
            navigate(`/${siteId}/cms/blog/new`);
        } else {
            navigate(`/${siteId}/cms/entries/${schemaId}/new`);
        }
    };

    const btnBase = {
        display: 'flex', alignItems: 'center', gap: '6px',
        border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px',
        fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 150ms ease',
    };

    if (loading) {
        return <CmsPageSkeleton />;
    }

    return (
        <div className="app-content animate-in">
            {/* Header */}
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h1 className="dashboard-title">
                        {schemaId === 'blog' ? <FiFileText size={20} /> : <FiDatabase size={20} />}
                        {schemaDisplayName}
                    </h1>
                    <p className="dashboard-subtitle">{filteredList.length} {filteredList.length === 1 ? 'entry' : 'entries'}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative' }}>
                        <FiSearch size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
                        <input
                            type="text"
                            placeholder="Search entries..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '200px', padding: '9px 12px 9px 36px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '13px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none' }}
                        />
                    </div>
                    <button
                        onClick={() => navigate(`/${siteId}/cms/schemas`)}
                        title="Manage Content Type"
                        style={{ ...btnBase, padding: '9px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    >
                        <FiSettings size={14} /> Schema
                    </button>
                    <button onClick={handleNewEntry} style={{ ...btnBase, padding: '9px 16px', background: 'var(--accent-gradient)', color: 'white' }}>
                        <FiPlus size={14} /> New Entry
                    </button>
                </div>
            </div>

            {/* Entries Table */}
            {filteredList.length === 0 && !loading2 ? (
                <div className="bento-card" style={{ textAlign: 'center', padding: '48px' }}>
                    {schemaId === 'blog'
                        ? <FiFileText size={36} style={{ color: 'var(--text-tertiary)', opacity: 0.4, marginBottom: '12px' }} />
                        : <FiDatabase size={36} style={{ color: 'var(--text-tertiary)', opacity: 0.4, marginBottom: '12px' }} />}
                    <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px', color: 'var(--text-primary)' }}>No {schemaDisplayName} Entries Yet</p>
                    <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>Create your first entry to get started.</p>
                    <button onClick={handleNewEntry} style={{ ...btnBase, display: 'inline-flex', padding: '9px 16px', background: 'var(--accent-gradient)', color: 'white' }}>
                        <FiPlus size={14} /> Create Entry
                    </button>
                </div>
            ) : loading2 ? (
                <div className="bento-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '48px', minHeight: '180px' }}>
                    <Spinner animation="border" style={{ width: '1.5rem', height: '1.5rem', color: 'var(--accent-primary)' }} />
                    <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Fetching latest entries...</span>
                </div>
            ) : (
                <div className="bento-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                <th style={{ padding: '11px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Title</th>
                                <th style={{ padding: '11px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px', width: '100px' }}>Status</th>
                                <th style={{ padding: '11px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px', width: '120px' }}>Created</th>
                                <th style={{ padding: '11px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px', width: '120px' }}>Updated</th>
                                <th style={{ padding: '11px 20px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px', width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.map((entry) => (
                                <tr
                                    key={entry.entry_id}
                                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                                    onClick={() => handleEditEntry(entry)}
                                >
                                    <td style={{ padding: '12px 20px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{entry.title || entry.data?.title || 'Untitled'}</div>
                                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-tertiary)' }}>/{entry.slug || entry.data?.slug || ''}</div>
                                    </td>
                                    <td style={{ padding: '12px 20px' }}>
                                        {entry.status === 'published'
                                            ? <span className="badge-modern badge-modern-success">Published</span>
                                            : <span className="badge-modern" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>Draft</span>}
                                    </td>
                                    <td style={{ padding: '12px 20px', fontSize: '12px', color: 'var(--text-tertiary)' }}>{formatDate(entry.created_at)}</td>
                                    <td style={{ padding: '12px 20px', fontSize: '12px', color: 'var(--text-tertiary)' }}>{formatDate(entry.updated_at)}</td>
                                    <td style={{ padding: '12px 20px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                                            <button
                                                title="Edit"
                                                onClick={() => handleEditEntry(entry)}
                                                disabled={actionLoading}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
                                            >
                                                <FiEdit3 size={14} />
                                            </button>
                                            <button
                                                title="Delete"
                                                onClick={() => { setSelectedEntry(entry); setShowDeleteModal(true); }}
                                                disabled={actionLoading}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: 'none', background: 'transparent', color: 'var(--error)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Entry</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete "{selectedEntry?.title || selectedEntry?.data?.title || 'this post'}"? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={actionLoading}>Cancel</Button>
                    <Button variant="danger" onClick={deleteEntry} disabled={actionLoading}>
                        {actionLoading ? <><Spinner size="sm" className="me-2" />Deleting...</> : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StCmsBlogEntries;
